import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

type Tier = "free" | "tutor" | "center" | "school" | "enterprise";

type TierConfig = {
  maxClasses: number;
  maxStudents: number;
  maxTeachers: number;
  dashboard: boolean;
  reports: boolean;
  finance: boolean;
  bulkNotify: boolean;
  exportExcel: boolean;
  exportPdf: boolean;
  scheduledNotify: boolean;
  documentsEnabled: boolean;
  teacherNotify: boolean;
};

const TIER_CONFIG: Record<Tier, TierConfig> = {
  free: {
    maxClasses: 1, maxStudents: 5, maxTeachers: 0,
    dashboard: false, reports: false, finance: false,
    bulkNotify: false, exportExcel: false, exportPdf: false, scheduledNotify: false,
    documentsEnabled: false, teacherNotify: false,
  },
  tutor: {
    maxClasses: 1, maxStudents: 10, maxTeachers: 0,
    dashboard: false, reports: false, finance: false,
    bulkNotify: false, exportExcel: false, exportPdf: false, scheduledNotify: false,
    documentsEnabled: false, teacherNotify: false,
  },
  center: {
    maxClasses: 10, maxStudents: 100, maxTeachers: Number.MAX_SAFE_INTEGER,
    dashboard: true, reports: true, finance: true,
    bulkNotify: true, exportExcel: true, exportPdf: false, scheduledNotify: true,
    documentsEnabled: true, teacherNotify: true,
  },
  school: {
    maxClasses: Number.MAX_SAFE_INTEGER, maxStudents: Number.MAX_SAFE_INTEGER, maxTeachers: Number.MAX_SAFE_INTEGER,
    dashboard: true, reports: true, finance: true,
    bulkNotify: true, exportExcel: true, exportPdf: true, scheduledNotify: true,
    documentsEnabled: true, teacherNotify: true,
  },
  enterprise: {
    maxClasses: Number.MAX_SAFE_INTEGER, maxStudents: Number.MAX_SAFE_INTEGER, maxTeachers: Number.MAX_SAFE_INTEGER,
    dashboard: true, reports: true, finance: true,
    bulkNotify: true, exportExcel: true, exportPdf: true, scheduledNotify: true,
    documentsEnabled: true, teacherNotify: true,
  },
};

function getAdminClient() {
  return createClient<Database>(process.env.SUPABASE_URL!, process.env.SUPABASE_SERVICE_ROLE_KEY!, {
    auth: { persistSession: false, autoRefreshToken: false },
  });
}

async function getTier(userId: string): Promise<Tier> {
  const admin = getAdminClient();
  const { data } = await admin
    .from("subscriptions")
    .select("status, current_period_end, plan")
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (!data) return "free";
  const now = Date.now();
  const periodEnd = data.current_period_end ? new Date(data.current_period_end).getTime() : Infinity;
  const active =
    (["active", "trialing", "past_due"].includes(data.status) && periodEnd > now) ||
    (data.status === "canceled" && periodEnd > now);
  if (!active || !data.plan) return "free";
  return (Object.keys(TIER_CONFIG).includes(data.plan) ? (data.plan as Tier) : "free");
}

async function countByRole(
  admin: ReturnType<typeof getAdminClient>,
  centerId: string,
  role: "student" | "teacher",
): Promise<number> {
  const { data: roleRows } = await admin.from("user_roles").select("user_id").eq("role", role);
  const ids = (roleRows ?? []).map((r) => r.user_id);
  if (!ids.length) return 0;
  const { count } = await admin
    .from("profiles")
    .select("id", { count: "exact", head: true })
    .eq("center_id", centerId)
    .in("id", ids);
  return count ?? 0;
}

export const getMyEntitlements = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const tier = await getTier(context.userId);
    const config = TIER_CONFIG[tier];
    const admin = getAdminClient();

    const { data: profile } = await admin
      .from("profiles")
      .select("center_id")
      .eq("id", context.userId)
      .maybeSingle();
    const centerId = profile?.center_id ?? null;

    let classCount = 0;
    let studentCount = 0;
    let teacherCount = 0;

    if (centerId) {
      const { data: centerProfiles } = await admin
        .from("profiles")
        .select("id")
        .eq("center_id", centerId);
      const centerProfileIds = (centerProfiles ?? []).map((p) => p.id);

      if (centerProfileIds.length > 0) {
        const { data: centerRoles } = await admin
          .from("user_roles")
          .select("user_id, role")
          .in("user_id", centerProfileIds);
        studentCount = (centerRoles ?? []).filter((r) => r.role === "student").length;
        teacherCount = (centerRoles ?? []).filter((r) => r.role === "teacher").length;
      }

      const { count: cc } = await admin
        .from("classes")
        .select("id", { count: "exact", head: true })
        .eq("center_id", centerId);
      classCount = cc ?? 0;
    }

    return {
      tier,
      dashboard: config.dashboard,
      reports: config.reports,
      finance: config.finance,
      bulkNotify: config.bulkNotify,
      exportExcel: config.exportExcel,
      exportPdf: config.exportPdf,
      scheduledNotify: config.scheduledNotify,
      documentsEnabled: config.documentsEnabled,
      teacherNotify: config.teacherNotify,
      maxClasses: config.maxClasses === Number.MAX_SAFE_INTEGER ? -1 : config.maxClasses,
      maxStudents: config.maxStudents === Number.MAX_SAFE_INTEGER ? -1 : config.maxStudents,
      maxTeachers: config.maxTeachers === Number.MAX_SAFE_INTEGER ? -1 : config.maxTeachers,
      currentClasses: classCount,
      currentStudents: studentCount,
      currentTeachers: teacherCount,
    };
  });

export const assertCanCreateClass = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const admin = getAdminClient();
    const tier = await getTier(context.userId);
    const limit = TIER_CONFIG[tier].maxClasses;
    const { data: profile } = await admin
      .from("profiles").select("center_id").eq("id", context.userId).maybeSingle();
    const centerId = profile?.center_id ?? "";
    const { count } = await admin
      .from("classes").select("id", { count: "exact", head: true }).eq("center_id", centerId);
    const current = count ?? 0;
    if (current >= limit) {
      throw new Error(
        `Đã đạt giới hạn ${limit === Number.MAX_SAFE_INTEGER ? "∞" : limit} lớp của gói ${tier}. Vui lòng nâng cấp để tạo thêm.`,
      );
    }
    return { ok: true, tier, current, limit };
  });

export const assertCanAddStudent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const admin = getAdminClient();
    const tier = await getTier(context.userId);
    const limit = TIER_CONFIG[tier].maxStudents;
    const { data: profile } = await admin
      .from("profiles").select("center_id").eq("id", context.userId).maybeSingle();
    const centerId = profile?.center_id;
    if (!centerId) throw new Error("Admin chưa có trung tâm");

    const { data: centerProfiles } = await admin
      .from("profiles").select("id").eq("center_id", centerId);
    const profileIds = (centerProfiles ?? []).map((p) => p.id);
    let current = 0;
    if (profileIds.length > 0) {
      const { data: studentRoles } = await admin
        .from("user_roles").select("user_id").eq("role", "student").in("user_id", profileIds);
      current = studentRoles?.length ?? 0;
    }
    if (current >= limit) {
      throw new Error(
        `Đã đạt giới hạn ${limit === Number.MAX_SAFE_INTEGER ? "∞" : limit} học viên của gói ${tier}. Vui lòng nâng cấp để thêm.`,
      );
    }
    return { ok: true, tier, current, limit };
  });

export const assertCanAddTeacher = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const tier = await getTier(context.userId);
    const limit = TIER_CONFIG[tier].maxTeachers;
    if (limit === 0) {
      throw new Error(
        "Gói của bạn không hỗ trợ tạo giáo viên. Admin sẽ được tự động gán làm giáo viên. Nâng cấp gói Trung tâm để thêm giáo viên.",
      );
    }
    return { ok: true };
  });
